import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Target, X } from "lucide-react";
import { toast } from "sonner";
import { getMyQualification, saveMyQualification } from "@/lib/qualification.functions";
import {
  INCOME_RANGES,
  DOWN_PAYMENT_SAVINGS,
  PURCHASE_TIMELINES,
  type QualificationAnswers,
} from "@/lib/stackQualification.shared";

const DISMISS_KEY = "qualification_prompt_dismissed_at";
const DISMISS_DAYS = 14;

type Field = "purchase_timeline" | "income_range" | "down_payment_savings";

const QUESTIONS: Record<Field, { question: string; options: { value: string; label: string }[] }> = {
  purchase_timeline: {
    question: "When are you hoping to buy?",
    options: PURCHASE_TIMELINES.map((t) => ({ value: t.value, label: t.label })),
  },
  income_range: {
    question: "Roughly what's your annual household income?",
    options: INCOME_RANGES.map((v) => ({ value: v, label: v })),
  },
  down_payment_savings: {
    question: "How much have you saved for a down payment so far?",
    options: DOWN_PAYMENT_SAVINGS.map((v) => ({ value: v, label: v })),
  },
};

const ORDER: Field[] = ["purchase_timeline", "income_range", "down_payment_savings"];

interface Props {
  /** "card" for the dashboard, "inline" above Stack results. */
  variant?: "card" | "inline";
  dismissible?: boolean;
}

export default function QualificationPrompt({ variant = "card", dismissible = true }: Props) {
  const fetchQualification = useServerFn(getMyQualification);
  const saveQualification = useServerFn(saveMyQualification);

  const [missing, setMissing] = useState<Field[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [justFinished, setJustFinished] = useState(false);
  const [hidden, setHidden] = useState(false);

  const dismissedRecently = useMemo(() => {
    if (!dismissible || typeof window === "undefined") return false;
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    return Number.isFinite(at) && Date.now() - at < DISMISS_DAYS * 86_400_000;
  }, [dismissible]);

  useEffect(() => {
    if (dismissedRecently) return;
    let alive = true;
    fetchQualification()
      .then((res: { missing: string[] }) => {
        if (!alive) return;
        setMissing(ORDER.filter((f) => res.missing.includes(f)));
      })
      .catch(() => {
        if (alive) setMissing([]);
      });
    return () => { alive = false; };
  }, [fetchQualification, dismissedRecently]);

  if (dismissedRecently || hidden) return null;
  if (missing === null) return null;
  if (missing.length === 0 && !justFinished) return null;

  const field = missing[0];

  const choose = async (value: string) => {
    if (!field || saving) return;
    setSaving(true);
    try {
      const res = await saveQualification({
        data: { answers: { [field]: value } as QualificationAnswers },
      });
      const rest = missing.filter((f) => f !== field);
      setMissing(rest);
      if (rest.length === 0) {
        setJustFinished(true);
        toast.success(
          (res as any)?.complete
            ? "Saved — your Stack now shows what you actually clear."
            : "Saved.",
        );
      }
    } catch {
      toast.error("Couldn't save that — try again");
    } finally {
      setSaving(false);
    }
  };

  const dismiss = () => {
    if (typeof window !== "undefined") window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
  };

  const answeredCount = ORDER.length - missing.length;

  const body = justFinished ? (
    <div className="flex items-start gap-3">
      <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
      <p className="text-sm text-muted-foreground">
        Thanks — your Stack results now separate what you clear today from what needs a little work.
      </p>
    </div>
  ) : (
    <>
      <div className="flex items-start gap-3">
        <Target className="h-5 w-5 text-primary mt-0.5 shrink-0" />
        <div>
          <h3 className="font-semibold text-foreground">
            Right now we can only tell you you're matched — not whether you qualify
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {ORDER.length - answeredCount === 1
              ? "One quick question and we'll label every program clears today, likely, or needs work."
              : `${ORDER.length - answeredCount} quick questions and we'll label every program clears today, likely, or needs work.`}
          </p>
        </div>
      </div>

      <p className="mt-4 text-sm font-medium text-foreground">{field ? QUESTIONS[field].question : ""}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {field &&
          QUESTIONS[field].options.map((o) => (
            <button
              key={o.value}
              type="button"
              disabled={saving}
              onClick={() => choose(o.value)}
              className="rounded-full border border-border bg-background px-3.5 py-2 text-sm text-foreground transition hover:border-primary/60 hover:bg-muted/40 disabled:opacity-60"
            >
              {o.label}
            </button>
          ))}
      </div>
      {saving && (
        <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
        </p>
      )}
    </>
  );

  if (variant === "inline") {
    return (
      <div className="relative rounded-lg border border-warm-tan bg-secondary/55 p-4">
        {dismissible && !justFinished && (
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {body}
      </div>
    );
  }

  return (
    <Card className="relative mb-6 border-primary/30">
      <CardContent className="pt-6">
        {dismissible && !justFinished && (
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {body}
      </CardContent>
    </Card>
  );
}
